import React from 'react';
import { Alert, Grid, Paper, Typography } from '@mui/material';
import type { UsageOverview } from '../types';
import { formatCount, formatDuration, formatNumber, formatPercent } from '../services/usageFormat';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — die sechs Kennzahlen des Überblicks.
 *
 * Jede Kachel trägt eine Unterzeile mit ihrer Regel. „Aktive Nutzer“ ist die Vereinigung der
 * Pseudonyme über den Zeitraum und nicht die Tagessumme; sind die Pseudonymlisten älterer Tage
 * bereits gelöscht (usersEstimated), ist die Zahl eine Untergrenze und sagt das auch.
 *
 * Die Fehlerquote nennt Zähler und Nenner, weil 1,3 % bei 6 310 Aufrufen etwas anderes bedeutet
 * als 1,3 % bei 60. Zurückrechnen ginge nicht: die Quote ist gerundet (RITA-1505 zog dafür
 * `apiErrors` additiv ins SDL nach).
 */
export interface UeberblickKachelnProps {
  overview: UsageOverview;
}

interface Kachel {
  titel: string;
  wert: string;
  unterzeile: string;
}

export default function UeberblickKacheln({ overview }: UeberblickKachelnProps) {
  if (overview.suppressed) {
    // Der Zeitraum selbst erfüllt die Mindestgruppe nicht — dann gibt es keine Kennzahlen,
    // und Nullen wären eine Behauptung über eine Handvoll Personen.
    return (
      <Alert severity="info" data-testid="nutzung-ueberblick-unterdrueckt">
        Für diesen Zeitraum sind zu wenige verschiedene Personen erfasst. Kennzahlen werden deshalb nicht ausgewiesen.
      </Alert>
    );
  }

  const kacheln: Kachel[] = [
    {
      titel: 'Aktive Nutzer',
      wert: formatCount(overview.activeUsers),
      unterzeile: overview.usersEstimated
        ? 'verschiedene Personen im Zeitraum · Untergrenze (ältere Pseudonyme gelöscht)'
        : 'verschiedene Personen im Zeitraum',
    },
    {
      titel: 'Sitzungen',
      wert: formatCount(overview.sessions),
      unterzeile: `${formatCount(overview.pageViews)} Seitenaufrufe insgesamt`,
    },
    {
      titel: 'Seiten je Sitzung',
      wert: formatNumber(overview.pagesPerSession),
      unterzeile: 'Durchschnitt',
    },
    {
      titel: 'Ø Sitzungsdauer',
      wert: formatDuration(overview.avgSessionMs),
      unterzeile: 'Sitzungsdauer, gedeckelt bei 12 h',
    },
    {
      titel: 'Anteil dunkel',
      wert: formatPercent(overview.darkShare),
      unterzeile: 'Darstellung „Dark“ je Sitzung',
    },
    {
      titel: 'Fehlerquote Server',
      wert: formatPercent(overview.apiErrorRate),
      unterzeile: `${formatCount(overview.apiErrors)} von ${formatCount(overview.apiCalls)} Aufrufen`,
    },
  ];

  return (
    <Grid container spacing={2}>
      {kacheln.map(({ titel, wert, unterzeile }) => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={titel}>
          <Paper
            variant="outlined"
            data-testid="nutzung-kachel"
            sx={{ p: 2, height: '100%', borderRadius: 2, bgcolor: 'background.default' }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
            >
              {titel}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
              {wert}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {unterzeile}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}
